/**
 * Cross-tab auth coordination via BroadcastChannel.
 *
 * Why this exists: with the move to HttpOnly cookies, every tab in the same browser
 * shares one refresh-token cookie but holds its OWN access token in memory. We need
 * to keep tabs in sync for two events the user notices:
 *
 *  1. Logout in tab A → tab B should sign out without waiting for its next 401.
 *  2. Refresh in tab A → tab B should swap its in-memory access token to the new
 *     one without making a redundant /refresh call.
 *
 * BroadcastChannel is supported in every modern browser (Safari ≥15.4, Chrome,
 * Firefox, Edge). For unsupported browsers we fall back to a noop — the logout will
 * still propagate on the next 401, just less promptly.
 */

const CHANNEL_NAME = 'taka-auth'

type AuthMessage = { type: 'logout' } | { type: 'token-refreshed'; accessToken: string }

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (channel) return channel
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    return channel
  } catch {
    return null
  }
}

export function broadcastLogout(): void {
  const ch = getChannel()
  if (!ch) return
  try {
    ch.postMessage({ type: 'logout' } satisfies AuthMessage)
  } catch {
    /* swallow — best-effort */
  }
}

export function broadcastTokenRefreshed(accessToken: string): void {
  const ch = getChannel()
  if (!ch) return
  try {
    ch.postMessage({ type: 'token-refreshed', accessToken } satisfies AuthMessage)
  } catch {
    /* swallow — best-effort */
  }
}

export function subscribeToAuthBroadcast(handler: (message: AuthMessage) => void): () => void {
  const ch = getChannel()
  if (!ch) return () => undefined
  const listener = (event: MessageEvent<AuthMessage>) => {
    if (event.data && typeof event.data.type === 'string') {
      handler(event.data)
    }
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}
